<section [formGroup]="group()" class="grid gap-8">
  <!-- What limits the client, a box for each of the three sides -->
  <div [formGroup]="group()">
    <h2 class="mb-1 text-lg font-semibold text-slate-900">Client's limitations in respect of the intervention</h2>
    <p class="mb-4 text-sm text-slate-500">
      What limits the client in taking part in the intervention, on each of the three sides. These are not the causes of the problem (those are
      in the clinical background).
    </p>
    <div formArrayName="limitations" class="grid gap-x-4 md:grid-cols-3">
      @for (row of limitationRows().controls; track $index; let index = $index) {
        <div [formGroupName]="index">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>{{ lookupName('clientLimitationTypes', row.value.clientLimitationTypeId) }}</mat-label>
            <textarea matInput rows="3" formControlName="details"></textarea>
          </mat-form-field>
        </div>
      }
    </div>
  </div>

  <!-- The symptom clusters, one line each until opened. A cluster with anything in it needs both ratings. -->
  <div [formGroup]="group()">
    <h2 class="mb-1 text-lg font-semibold text-slate-900">Symptom clusters</h2>
    <p class="mb-3 text-sm text-slate-500">
      The full scope of the member's underlying presentation. Open the clusters that apply: how much each affects the current problem, and how much
      the recommended intervention would help. The others can be left empty.
    </p>
    <mat-accordion multi formArrayName="symptomClusters">
      @for (row of clusterRows().controls; track $index; let index = $index) {
        <mat-expansion-panel
          [formGroupName]="index"
          [expanded]="clusterOpen(index, row)"
          (opened)="setClusterOpen(index, true)"
          (closed)="setClusterOpen(index, false)">
          <mat-expansion-panel-header>
            <mat-panel-title>{{ row.value.symptomCluster }}</mat-panel-title>
            <mat-panel-description>
              @if (clusterUsed(row)) {
                <span class="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Filled in</span>
              } @else {
                <span class="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">Not filled in</span>
              }
            </mat-panel-description>
          </mat-expansion-panel-header>
          <div class="grid gap-x-8 gap-y-3 md:grid-cols-2">
            <div class="grid content-start gap-3">
              <mat-form-field appearance="outline" class="w-full">
                <mat-label>Client's presentation</mat-label>
                <textarea matInput rows="2" formControlName="clientPresentation"></textarea>
              </mat-form-field>
              <scale-slider
                label="Level of impact on the current problem"
                formControlName="currentProblemImpactRating"
                [options]="options('severityScales')"
                [numeric]="true"
                [minValue]="1"
                [clearable]="true" />
            </div>
            <div class="grid content-start gap-3">
              <mat-form-field appearance="outline" class="w-full">
                <mat-label>Intervention recommendations</mat-label>
                <textarea matInput rows="2" formControlName="interventionRecommendations"></textarea>
              </mat-form-field>
              <scale-slider
                label="Level of impact of the intervention"
                formControlName="interventionImpactRating"
                [options]="options('severityScales')"
                [numeric]="true"
                [minValue]="1"
                [clearable]="true" />
            </div>
          </div>
          @if (row.hasError('ratingsRequired') && !readOnly()) {
            <p class="mt-2 text-sm text-red-600">Give both ratings for this cluster, or clear what is written in it.</p>
          }
        </mat-expansion-panel>
      }
    </mat-accordion>
  </div>

  <!-- The clinician's impressions, and the treatment plan that follows from them; the goals are entered below, in the same step -->
  <div [formGroup]="group()" class="grid gap-6">
    <div>
      <h2 class="mb-1 text-lg font-semibold text-slate-900">Clinical impressions</h2>
      <p class="mb-4 text-sm text-slate-500">What the clinician makes of the presentation, in the clinician's words.</p>
      <mat-form-field appearance="outline" class="w-full">
        <mat-label>Clinical impressions</mat-label>
        <textarea matInput rows="5" formControlName="clinicalImpressions"></textarea>
      </mat-form-field>
    </div>
    <div>
      <h2 class="mb-1 text-lg font-semibold text-slate-900">Treatment plan summary</h2>
      <p class="mb-4 text-sm text-slate-500">The full scope of recommendations for the member.</p>
      <mat-form-field appearance="outline" class="w-full">
        <mat-label>Treatment plan summary</mat-label>
        <textarea matInput rows="5" formControlName="treatmentPlanSummary"></textarea>
      </mat-form-field>
    </div>
  </div>
</section>
